import { Suspense, lazy, useState } from "react";
import Layout from "./components/layout/Layout";
import Header from "./components/layout/Header";
import HeroSection from "./components/sections/HeroSection";
import ScheduleConsultation from "./components/sections/ScheduleConsultation";
import Response from "./components/layout/Response";
import BackToTop from "./components/ui/BackToTop";

// Lazy load heavy components for better performance
const ExpertiseSection = lazy(
  () => import("./components/sections/ExpertiseSection")
);
const SolutionsSection = lazy(
  () => import("./components/sections/SolutionsSection")
);
const ApproachSection = lazy(
  () => import("./components/sections/ApproachSection")
);
const FAQSection = lazy(
  () => import("./components/sections/FAQSection")
);

// Loading component for suspense fallback
const SectionLoader = () => (
  <div className="flex items-center justify-center py-16">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white/20"></div>
  </div>
);

import HeroParticles from "./components/ui/HeroParticles";

function App() {
  const [showResponse, setShowResponse] = useState(false);

  return (
    <>
      <a
        href="#expertise"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[60] focus:px-4 focus:py-2 focus:bg-white focus:text-black focus:rounded-md focus:text-sm"
      >
        Skip to content
      </a>
      <Layout>
        {/* Hero Section */}
        <section className="relative min-h-[70vh] overflow-hidden">
          <HeroParticles />

          {/* Ambient glow accents */}
          <div className="absolute top-[10%] right-[-5%] w-[50%] h-[70%] rounded-full bg-indigo-600/15 blur-[120px] pointer-events-none" />
          <div className="absolute bottom-[5%] right-[10%] w-[35%] h-[50%] rounded-full bg-purple-600/12 blur-[100px] pointer-events-none" />

          {/* Dark fade behind text area */}
          <div className="absolute inset-0 bg-gradient-to-r from-surface/90 via-surface/40 to-transparent z-[1] pointer-events-none" />

          {/* Hero content */}
          <div className="relative z-10">
            <div className="container-2xl max-w-7xl mx-auto px-4 xl:px-8 py-16">
              <Header />
              <HeroSection />
            </div>
          </div>
        </section>

        {/* Main Content with solid background */}
        <main className="bg-surface relative z-20">
          <div className="container-2xl max-w-7xl mx-auto px-4 xl:px-8 py-16">
            <div className="divider mb-16"></div>

            {/* Lazy-loaded sections with suspense boundaries */}
            <Suspense fallback={<SectionLoader />}>
              <ExpertiseSection />
            </Suspense>

            <div className="divider mb-16"></div>

            <Suspense fallback={<SectionLoader />}>
              <SolutionsSection />
            </Suspense>

            <div className="divider mb-16"></div>

            <Suspense fallback={<SectionLoader />}>
              <ApproachSection />
            </Suspense>

            <div className="divider mb-16"></div>

            <Suspense fallback={<SectionLoader />}>
              <FAQSection />
            </Suspense>

            <div className="divider mb-16"></div>

            <Suspense fallback={<SectionLoader />}>
              {showResponse ? (
                <Response onReset={() => setShowResponse(false)} />
              ) : (
                <ScheduleConsultation onSuccess={() => setShowResponse(true)} />
              )}
            </Suspense>
          </div>
        </main>
      </Layout>
      <BackToTop />
    </>
  );
}

export default App;
